/**
 * OperacionCompraVentaRepository - Repositorio unificado para Operaciones de Compra/Venta
 *
 * Implementación compartida entre web (Dexie) y mobile (SQLite).
 */

import { v4 as uuidv4 } from 'uuid';
import { IOperacionCompraVentaRepository } from '../interfaces/IOperacionCompraVentaRepository';
import { OperacionCompraVentaAnimales } from '../../../types/interfaces';
import { BaseRepository } from './BaseRepository';
import { IDatabaseAdapter } from '../adapters/IDatabaseAdapter';
import { IProfileRepository } from '../interfaces/IProfileRepository';

/**
 * Repositorio unificado de Operaciones de Compra/Venta
 */
export class OperacionCompraVentaRepository extends BaseRepository<OperacionCompraVentaAnimales, string> implements IOperacionCompraVentaRepository {

  constructor(db: IDatabaseAdapter, profileRepo?: IProfileRepository) {
    super(db, 'operacion_compra_venta_animales', 'operacion_compra_venta_animales');

    if (profileRepo) {
      this.setProfileRepository(profileRepo);
    }

    // Habilitar funcionalidades de BaseRepository
    this.supportsSoftDelete = true;
  }

  /** PK de operacion_compra_venta_animales es `uuid` (no `id`). */
  protected get pkField(): string {
    return 'uuid';
  }

  async add(data: OperacionCompraVentaAnimales): Promise<string> {
    const now = this.getCurrentTimestamp();
    const profile = await this.getCurrentProfile();

    // Use provided UUID if available, otherwise generate new one
    const id = data.uuid || uuidv4();

    const record: OperacionCompraVentaAnimales = {
      ...data,
      uuid: id,
      empresa_id: profile?.empresa_activa_id || '',
      created_by: profile?.user_id || data.created_by || '',
      updated_by: profile?.user_id || data.updated_by || '',
      fecha_de_creacion_cliente: now,
      is_deleted: false
    };

    await this.db.insert('operacion_compra_venta_animales', record);
    this.emitSync('INSERT', record);

    return id;
  }

  async get(uuid: string): Promise<OperacionCompraVentaAnimales | undefined> {
    return this.db.get<OperacionCompraVentaAnimales>('operacion_compra_venta_animales', uuid);
  }

  // @ts-ignore - OperacionCompraVentaAnimales uses 'uuid' instead of 'id'
  async update(data: any): Promise<void> {
    await this.db.update('operacion_compra_venta_animales', data.uuid, data);
    this.emitSync('UPDATE', data as any);
  }

  // @ts-ignore - OperacionCompraVentaAnimales uses 'uuid' instead of 'id'
  async delete(uuid: string): Promise<void> {
    await this.db.delete('operacion_compra_venta_animales', uuid);
    this.emitSync('DELETE', uuid);
  }

  // @ts-ignore - OperacionCompraVentaAnimales uses 'uuid' instead of 'id'
  async softDelete(uuid: string): Promise<void> {
    const profile = await this.getCurrentProfile();
    const updatedData = {
      empresa_id: profile?.empresa_activa_id || '',
      updated_by: profile?.user_id || '',
      is_deleted: true
    };

    console.log('[SOFT DELETE] Actualizando operación localmente:', uuid, updatedData);
    await this.db.update('operacion_compra_venta_animales', uuid, updatedData);

    const syncPayload = { uuid, ...updatedData };
    console.log('[SOFT DELETE] Enviando evento de sincronización:', syncPayload);
    this.emitSync('SOFT_DELETE', syncPayload as any);
  }

  // @ts-ignore - OperacionCompraVentaAnimales uses 'uuid' instead of 'id'
  async restore(uuid: string): Promise<void> {
    await this.db.update('operacion_compra_venta_animales', uuid, { is_deleted: false });
    this.emitSync('RESTORE', { uuid, is_deleted: false } as any);
  }

  async getDeleted(): Promise<OperacionCompraVentaAnimales[]> {
    return this.db.query<OperacionCompraVentaAnimales>('operacion_compra_venta_animales', {
      field: 'is_deleted',
      operator: 'equals',
      value: true
    });
  }

  async getByFecha(fecha: string): Promise<OperacionCompraVentaAnimales[]> {
    return this.db.queryMultiple<OperacionCompraVentaAnimales>('operacion_compra_venta_animales', [
      { field: 'fecha_de_evento', operator: 'equals', value: fecha },
      { field: 'is_deleted', operator: 'equals', value: false }
    ]);
  }

  async getByTipo(esVenta: boolean): Promise<OperacionCompraVentaAnimales[]> {
    const all = await this.getAllForActiveEmpresa();
    return all.filter(op => op.es_venta === esVenta && !op.is_deleted);
  }

  async getByPeriodo(fechaInicio: string, fechaFin: string): Promise<OperacionCompraVentaAnimales[]> {
    // Filtramos por empresa activa para no mezclar datos de otras empresas.
    const all = await this.getAllForActiveEmpresa();
    return all.filter(op => {
      if (op.is_deleted) return false;
      return op.fecha_de_evento >= fechaInicio && op.fecha_de_evento <= fechaFin;
    });
  }

  async getVentasByPeriodo(fechaInicio: string, fechaFin: string): Promise<OperacionCompraVentaAnimales[]> {
    // Filtramos por empresa activa para no mezclar datos de otras empresas.
    const all = await this.getAllForActiveEmpresa();
    return all.filter(op => {
      return !op.is_deleted &&
        op.es_venta === true &&
        op.fecha_de_evento >= fechaInicio &&
        op.fecha_de_evento <= fechaFin;
    });
  }

  // ==================== Bulk Operations ====================
  // Estos métodos ahora vienen de BaseRepository

  // ==================== Fin ====================
}
